import { useCallback, useEffect, useRef, useState } from 'react';

import { Virtualizer } from '@tanstack/react-virtual';

import { MensagemInterface } from 'store/reducers/mensagens';
import { formatDate } from 'utils/formatDate';

type VirtualItem = {
  type: 'message' | 'group';
  data: MensagemInterface | MensagemInterface[];
  originalIndex: number;
  messageIds: string[];
};

interface UseVisibilityDateMessageProps {
  rowVirtualizer: Virtualizer<HTMLUListElement, Element>;
  items: VirtualItem[];
}

export const useVisibilityDateMessage = ({
  rowVirtualizer,
  items,
}: UseVisibilityDateMessageProps) => {
  const [dataVisivel, setDataVisivel] = useState<string | null>(null);
  const [visible, setVisible] = useState(true);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Busca a primeira mensagem de um grupo
  const getFirstMessage = useCallback(
    (item: VirtualItem): MensagemInterface | null => {
      if (item.type === 'message') {
        return item.data as MensagemInterface;
      }
      const grupo = item.data as MensagemInterface[];
      return grupo[0] || null;
    },
    [],
  );

  // Atualiza data visível e reinicia timeout sempre que o range muda
  useEffect(() => {
    if (!rowVirtualizer.range) return;

    const startIndex = Math.min(
      rowVirtualizer.range.startIndex,
      items.length - 1,
    );

    const item = items[startIndex];
    if (!item) return;

    const mensagem = getFirstMessage(item);
    if (!mensagem?.criadoEm) return;

    const novaData = formatDate(new Date(mensagem.criadoEm));

    setDataVisivel(prev => (prev !== novaData ? novaData : prev));

    setVisible(true);
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    hideTimeoutRef.current = setTimeout(() => setVisible(false), 2000);
  }, [rowVirtualizer.range, items, getFirstMessage]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  return { dataVisivel, visible };
};
